import { useEffect, useId, useRef } from 'react';
import gsap from 'gsap';
import styles from './dropdown.module.scss';
import cn from 'classnames';

/**
 * Acordeón de una pregunta frecuente.
 *
 * El estado abierto/cerrado se expone al árbol de accesibilidad: sin
 * `aria-expanded` el lector de pantalla no anuncia nada al activar la pregunta, y
 * el contenido colapsado sigue en el DOM (`height: 0`), así que sin marcarlo el
 * lector lee las respuestas de todas las preguntas de corrido.
 *
 * Se marca con `inert` y no con `aria-hidden` porque una respuesta puede traer
 * links (`PrismicRichText`): `aria-hidden` los sacaría del árbol dejándolos
 * tabulables, que es una falla de accesibilidad en sí misma. `inert` los saca de
 * los dos lados. Se aplica por ref porque React 18 no lo pasa como prop.
 */
const Dropdown = ({ question, children, isActive, toggle }) => {
  const dropdownArrow = useRef(null);
  const dropdownContent = useRef(null);
  const baseId = useId();
  const buttonId = `${baseId}-faq-button`;
  const contentId = `${baseId}-faq-content`;

  useEffect(() => {
    if (dropdownContent.current) dropdownContent.current.inert = !isActive;

    if (isActive) {
      gsap.to(dropdownContent.current, {
        height: 'auto',
        ease: 'expo.out',
        duration: 0.7
      });
      gsap.to(dropdownArrow.current, {
        rotateX: '-180deg',
        ease: 'expo.out',
        duration: 0.7
      });
    } else {
      gsap.to(dropdownContent.current, {
        height: 0,
        ease: 'expo.out',
        duration: 0.7
      });
      gsap.to(dropdownArrow.current, {
        rotateX: 0,
        ease: 'expo.out',
        duration: 0.7
      });
    }
  }, [isActive]);

  return (
    <div className="relative w-full group">
      <button
        id={buttonId}
        onClick={toggle}
        type="button"
        aria-label={question}
        aria-expanded={isActive}
        aria-controls={contentId}
        className="relative w-full py-24 flex items-center justify-between"
      >
        <div className="relative">
          <h3
            className={cn('title-h6 text-left duration-40', {
              '!translate-x-[1rem] sm:!translate-x-0': isActive
            })}
          >
            {question}
          </h3>
          <div
            className={cn(
              'absolute -left-20 top-1/2 -translate-y-1/2 translate-x-[2rem] opacity-0 sm:group-hover:opacity-100 sm:group-hover:translate-x-0 duration-40',
              {
                'sm:!opacity-100 !translate-x-[2rem] sm:!translate-x-0': isActive
              }
            )}
          >
            <svg
              className="w-12 h-auto"
              viewBox="0 0 12 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.76783 0.718671C5.16395 0.737362 5.54171 0.836156 5.90933 0.968327C6.38709 1.13966 6.83586 1.36351 7.26966 1.61316C8.10248 2.09245 8.8754 2.64739 9.63286 3.22058C10.299 3.72479 10.9642 4.22945 11.6308 4.73321C11.7337 4.81109 11.8419 4.88318 11.9477 4.95795C11.9651 4.96996 11.9816 4.98331 12.0004 4.99755C11.8149 5.13195 11.6313 5.26056 11.4526 5.3954C10.8995 5.81328 10.3488 6.23427 9.79662 6.65348C9.10341 7.17949 8.40102 7.69438 7.65467 8.15543C7.12957 8.4794 6.58708 8.77534 6.00063 8.99607C5.49195 9.18743 4.96782 9.30892 4.41229 9.28222C3.79782 9.25285 3.20992 9.12557 2.6539 8.87992C1.95489 8.57152 1.37327 8.13184 0.912906 7.55776C0.461233 6.99481 0.176703 6.36555 0.0588334 5.67488C-0.0633841 4.95617 0.00424609 4.25036 0.267038 3.56325C0.5535 2.81383 1.03078 2.18502 1.695 1.67903C2.24667 1.25893 2.87177 0.972334 3.56932 0.821916C3.96303 0.736917 4.36301 0.69998 4.76638 0.719116L4.76783 0.718671Z"
                fill="#064246"
              />
            </svg>
          </div>
        </div>
        <div className="ml-40 sm:ml-0">
          <svg
            ref={dropdownArrow}
            width="21"
            height="11"
            viewBox="0 0 21 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1 0.337891L10.5 10.3379L20 0.337891" stroke="#064246" strokeWidth="0.7" />
          </svg>
        </div>
      </button>
      <div
        ref={dropdownContent}
        id={contentId}
        role="region"
        aria-labelledby={buttonId}
        className="h-0 overflow-hidden"
      >
        <div className={`pb-24 text-black opacity-60 ${styles.content}`}>{children}</div>
      </div>
      <div className="w-full h-1 bg-cyan opacity-30"></div>
    </div>
  );
};
export default Dropdown;
